import { create } from 'zustand';
import { AgencyDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/AgencyDashboard/store/agencyDashboard.store.types';
import { devtoolsInNonProd } from 'store/zustand/index.utils';
import {
  AgencyMetricResponse,
  AgencyRateResponse,
  AgencyShiftsByPositionResponse,
  AgencyShiftsByPositionResponseItem,
  AgencyPositionShifts,
  AgencyHoursDistributionResponse,
  AgencyHoursDistributionPoint,
  AgencyHoursByProviderResponse,
  AgencyHoursByProviderResponseItem,
  AgencyProviderHours,
  LeadTimeBeforeShiftStartResponse,
  LeadTimeBucket,
  AgencyShiftsByTimeframeResponse,
  AgencyTimeframeCell,
  ShiftsSentBySchedulerResponse,
  SchedulerShiftsSent,
} from 'pages/ReportsPage/components/AnalyticsDashboards/AgencyDashboard/AgencyDashboard.types';
import { toNumber } from 'pages/ReportsPage/components/AnalyticsDashboards/calloutMappers';
import { createDashboardsRequest } from 'pages/ReportsPage/components/AnalyticsDashboards/createDashboardsRequest';
import { unwrapDashboardRoot } from 'pages/ReportsPage/components/AnalyticsDashboards/unwrapDashboardRoot';
import { fetchDashboardPage } from 'pages/ReportsPage/components/AnalyticsDashboards/fetchDashboardPage';
import {
  hasDataLockRejection,
  isDataLockError,
} from 'pages/ReportsPage/store/reportsDataHealth.store';
import { AGENCY_DATA_LOCKED_STUB } from 'pages/ReportsPage/components/AnalyticsDashboards/AgencyDashboard/store/agencyDashboard.stub';
import {
  DASHBOARDS_AGENCY_TOTAL_AGENCY_HOURS,
  DASHBOARDS_AGENCY_TOTAL_AGENCY_SHIFTS,
  DASHBOARDS_AGENCY_SENT_TO_AGENCY_RATE,
  DASHBOARDS_AGENCY_SHIFTS_BY_POSITION,
  DASHBOARDS_AGENCY_HOURS_DISTRIBUTION,
  DASHBOARDS_AGENCY_HOURS_BY_PROVIDER,
  DASHBOARDS_AGENCY_LEAD_TIME_BEFORE_SHIFT_START,
  DASHBOARDS_AGENCY_SHIFTS_BY_TIMEFRAME,
  DASHBOARDS_AGENCY_SHIFTS_SENT_BY_SCHEDULER,
} from 'constants/endpoints';
import { isSelectionCleared } from 'pages/ReportsPage/store/analyticsSelection.utils';

function buildFilterQuery(
  dateRange: [string, string],
  locationIds: string[] | null,
  positions: string[] | null,
  employmentTypes: string[],
): Record<string, unknown> {
  const filters: Record<string, unknown> = {
    time_range: {
      start_time: dateRange[0],
      end_time: dateRange[1],
    },
  };

  if (locationIds && locationIds.length > 0) {
    filters.community_ids = locationIds.map(Number);
  }
  if (positions && positions.length > 0) {
    filters.position_ids = positions.map(Number);
  }
  if (employmentTypes.length > 0) {
    filters.employment_statuses = employmentTypes;
  }

  return { filters };
}

function mapShiftsByPosition(
  response: AgencyShiftsByPositionResponseItem[],
): AgencyPositionShifts[] {
  return response.map((item) => ({
    positionId: String(item.position_id),
    position: item.position_name,
    shiftCount: item.shift_count,
    hours: toNumber(item.hours),
  }));
}

function mapHoursDistribution(
  response: AgencyHoursDistributionResponse,
): AgencyHoursDistributionPoint[] {
  return (response?.agency_hours_distribution?.buckets ?? []).map((bucket) => ({
    bucketStart: bucket.bucket_start,
    hours: toNumber(bucket.hours),
    sentToAgencyRate:
      bucket.sent_to_agency_rate == null ? null : toNumber(bucket.sent_to_agency_rate),
  }));
}

function mapHoursByProvider(response: AgencyHoursByProviderResponseItem[]): AgencyProviderHours[] {
  return response.map((item) => ({
    providerId: String(item.provider_id),
    provider: item.provider_name,
    shiftCount: item.shift_count,
    hours: toNumber(item.hours),
  }));
}

const LEAD_TIME_BUCKETS: {
  key: keyof LeadTimeBeforeShiftStartResponse['lead_time_before_shift_start'];
  label: string;
}[] = [
  { key: 'under_4h', label: '< 4h (last minute)' },
  { key: '4_to_12h', label: '4 – 12h' },
  { key: '12_to_24h', label: '12 – 24h' },
  { key: '1_to_3_days', label: '1 – 3 days' },
  { key: '3_to_7_days', label: '3 – 7 days' },
  { key: 'over_7_days', label: '> 7 days' },
];

function mapLeadTime(response: LeadTimeBeforeShiftStartResponse): LeadTimeBucket[] {
  const buckets = response?.lead_time_before_shift_start;
  return LEAD_TIME_BUCKETS.map(({ key, label }) => ({
    key,
    label,
    count: toNumber(buckets?.[key]?.count),
    percentage: toNumber(buckets?.[key]?.percentage),
  }));
}

const TIMEFRAME_LABELS: Record<string, string> = {
  morning: 'Morning',
  evening: 'Evening',
  night: 'Overnight',
};

const DAY_LABELS: {
  key: keyof AgencyShiftsByTimeframeResponse['agency_shifts_by_timeframe'][number]['days'];
  label: string;
}[] = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

function mapShiftsByTimeframe(response: AgencyShiftsByTimeframeResponse): AgencyTimeframeCell[] {
  const rows = response?.agency_shifts_by_timeframe ?? [];
  return rows.flatMap((row) =>
    DAY_LABELS.map(({ key, label }) => ({
      timeframe: TIMEFRAME_LABELS[row.shift_timeframe] ?? row.shift_timeframe,
      dayOfWeek: label,
      shiftCount: toNumber(row.days?.[key]),
    })),
  );
}

function mapShiftsSentByScheduler(response: ShiftsSentBySchedulerResponse): SchedulerShiftsSent[] {
  return (response?.shifts_sent_to_agency_by_scheduler ?? []).map((item) => ({
    schedulerId: item.scheduler_id,
    scheduler: item.scheduler_name ?? '—',
    location: item.location_name,
    shiftCount: item.shift_count,
    hours: toNumber(item.hours),
    averageLeadHours: toNumber(item.average_lead_hours),
  }));
}

const INITIAL_STATE = {
  totalHours: 0,
  totalHoursPeriodDays: null as number | null,
  totalHoursChangePrct: null as number | null,
  totalHoursError: false,
  totalShifts: 0,
  totalShiftsPeriodDays: null as number | null,
  totalShiftsChangePrct: null as number | null,
  totalShiftsError: false,
  sentToAgencyRate: 0,
  sentToAgencyRatePeriodDays: null as number | null,
  sentToAgencyRateChangePrct: null as number | null,
  sentToAgencyRateError: false,
  shiftsByPosition: null,
  shiftsByPositionPage: 1,
  shiftsByPositionTotalPages: 1,
  shiftsByPositionLoadingMore: false,
  shiftsByPositionError: false,
  hoursDistribution: null,
  hoursDistributionError: false,
  hoursByProvider: null,
  hoursByProviderError: false,
  leadTime: null,
  leadTimeError: false,
  shiftsByTimeframe: null,
  shiftsByTimeframeError: false,
  shiftsSentByScheduler: null,
  shiftsSentBySchedulerPage: 1,
  shiftsSentBySchedulerTotalPages: 1,
  shiftsSentBySchedulerLoading: false,
  shiftsSentBySchedulerError: false,
  isLoading: false,
};

let fetchId = 0;
let shiftsSentBySchedulerFetchId = 0;
let shiftsByPositionBaseQuery: Record<string, unknown> | null = null;

export const agencyDashboardStore = create<AgencyDashboardStore>()(
  devtoolsInNonProd((set, get) => ({
    ...INITIAL_STATE,

    fetchData: (dateRange, locationIds, positions, employmentTypes) => {
      if (isSelectionCleared(locationIds, positions)) {
        fetchId++;
        set({ ...INITIAL_STATE });
        return () => {
          fetchId++;
        };
      }
      const currentFetchId = ++fetchId;
      set({
        isLoading: true,
        totalHoursError: false,
        totalShiftsError: false,
        sentToAgencyRateError: false,
        shiftsByPositionError: false,
        hoursDistributionError: false,
        hoursByProviderError: false,
        leadTimeError: false,
        shiftsByTimeframeError: false,
      });

      const query = buildFilterQuery(dateRange, locationIds, positions, employmentTypes);
      shiftsByPositionBaseQuery = query;

      const totalHoursRequest = createDashboardsRequest<AgencyMetricResponse>({
        url: DASHBOARDS_AGENCY_TOTAL_AGENCY_HOURS(),
        method: 'GET',
        query,
      });

      const totalShiftsRequest = createDashboardsRequest<AgencyMetricResponse>({
        url: DASHBOARDS_AGENCY_TOTAL_AGENCY_SHIFTS(),
        method: 'GET',
        query,
      });

      const sentToAgencyRateRequest = createDashboardsRequest<AgencyRateResponse>({
        url: DASHBOARDS_AGENCY_SENT_TO_AGENCY_RATE(),
        method: 'GET',
        query,
      });

      const shiftsByPositionRequest = fetchDashboardPage<
        AgencyShiftsByPositionResponse,
        AgencyShiftsByPositionResponseItem
      >({
        url: DASHBOARDS_AGENCY_SHIFTS_BY_POSITION(),
        query,
        page: 1,
        selectItems: (body) => body.agency_shifts_by_position ?? [],
      });

      const hoursDistributionRequest = createDashboardsRequest<AgencyHoursDistributionResponse>({
        url: DASHBOARDS_AGENCY_HOURS_DISTRIBUTION(),
        method: 'GET',
        query,
      });

      const hoursByProviderRequest = createDashboardsRequest<AgencyHoursByProviderResponse>({
        url: DASHBOARDS_AGENCY_HOURS_BY_PROVIDER(),
        method: 'GET',
        query: { ...query, page: 1 },
      });

      const leadTimeRequest = createDashboardsRequest<LeadTimeBeforeShiftStartResponse>({
        url: DASHBOARDS_AGENCY_LEAD_TIME_BEFORE_SHIFT_START(),
        method: 'GET',
        query,
      });

      const shiftsByTimeframeRequest = createDashboardsRequest<AgencyShiftsByTimeframeResponse>({
        url: DASHBOARDS_AGENCY_SHIFTS_BY_TIMEFRAME(),
        method: 'GET',
        query,
      });

      get().fetchShiftsSentByScheduler(1, dateRange, locationIds, positions, employmentTypes);

      Promise.allSettled([
        totalHoursRequest,
        totalShiftsRequest,
        sentToAgencyRateRequest,
        shiftsByPositionRequest,
        hoursDistributionRequest,
        hoursByProviderRequest,
        leadTimeRequest,
        shiftsByTimeframeRequest,
      ]).then((results) => {
        const isDataLocked = hasDataLockRejection(results);
        if (currentFetchId !== fetchId) return;
        if (isDataLocked) {
          set({ ...INITIAL_STATE, ...AGENCY_DATA_LOCKED_STUB, isLoading: false });
          return;
        }
        const [
          totalHoursResult,
          totalShiftsResult,
          sentToAgencyRateResult,
          shiftsByPositionResult,
          hoursDistributionResult,
          hoursByProviderResult,
          leadTimeResult,
          shiftsByTimeframeResult,
        ] = results;

        const totalHoursData = unwrapDashboardRoot<AgencyMetricResponse>(
          totalHoursResult.status === 'fulfilled' ? totalHoursResult.value.data : null,
          'total_agency_hours',
        );
        const totalShiftsData = unwrapDashboardRoot<AgencyMetricResponse>(
          totalShiftsResult.status === 'fulfilled' ? totalShiftsResult.value.data : null,
          'total_agency_shifts',
        );
        const sentToAgencyRateData = unwrapDashboardRoot<AgencyRateResponse>(
          sentToAgencyRateResult.status === 'fulfilled' ? sentToAgencyRateResult.value.data : null,
          'sent_to_agency_rate',
        );
        const shiftsByPositionData =
          shiftsByPositionResult.status === 'fulfilled' ? shiftsByPositionResult.value : null;
        const hoursDistributionData =
          hoursDistributionResult.status === 'fulfilled'
            ? hoursDistributionResult.value.data
            : null;
        const hoursByProviderData =
          hoursByProviderResult.status === 'fulfilled' ? hoursByProviderResult.value.data : null;
        const leadTimeData =
          leadTimeResult.status === 'fulfilled' ? leadTimeResult.value.data : null;
        const shiftsByTimeframeData =
          shiftsByTimeframeResult.status === 'fulfilled'
            ? shiftsByTimeframeResult.value.data
            : null;

        set({
          totalHours: toNumber(totalHoursData?.current_period_total),
          totalHoursPeriodDays: totalHoursData?.period_days ?? null,
          totalHoursChangePrct:
            totalHoursData?.change_prct == null ? null : toNumber(totalHoursData.change_prct),
          totalHoursError: totalHoursResult.status === 'rejected',
          totalShifts: toNumber(totalShiftsData?.current_period_total),
          totalShiftsPeriodDays: totalShiftsData?.period_days ?? null,
          totalShiftsChangePrct:
            totalShiftsData?.change_prct == null ? null : toNumber(totalShiftsData.change_prct),
          totalShiftsError: totalShiftsResult.status === 'rejected',
          sentToAgencyRate: toNumber(sentToAgencyRateData?.current_period_rate),
          sentToAgencyRatePeriodDays: sentToAgencyRateData?.period_days ?? null,
          sentToAgencyRateChangePrct:
            sentToAgencyRateData?.change_prct == null
              ? null
              : toNumber(sentToAgencyRateData.change_prct),
          sentToAgencyRateError: sentToAgencyRateResult.status === 'rejected',
          shiftsByPosition: shiftsByPositionData
            ? mapShiftsByPosition(shiftsByPositionData.items)
            : null,
          shiftsByPositionPage: shiftsByPositionData?.meta?.current_page ?? 1,
          shiftsByPositionTotalPages: shiftsByPositionData?.meta?.total_pages ?? 1,
          shiftsByPositionLoadingMore: false,
          shiftsByPositionError: shiftsByPositionResult.status === 'rejected',
          hoursDistribution: hoursDistributionData
            ? mapHoursDistribution(hoursDistributionData)
            : null,
          hoursDistributionError: hoursDistributionResult.status === 'rejected',
          hoursByProvider: hoursByProviderData
            ? mapHoursByProvider(hoursByProviderData.agency_hours_by_provider ?? [])
            : null,
          hoursByProviderError: hoursByProviderResult.status === 'rejected',
          leadTime: leadTimeData ? mapLeadTime(leadTimeData) : null,
          leadTimeError: leadTimeResult.status === 'rejected',
          shiftsByTimeframe: shiftsByTimeframeData
            ? mapShiftsByTimeframe(shiftsByTimeframeData)
            : null,
          shiftsByTimeframeError: shiftsByTimeframeResult.status === 'rejected',
          isLoading: false,
        });
      });

      return () => {
        fetchId++;
      };
    },
    fetchShiftsSentByScheduler: (page, dateRange, locationIds, positions, employmentTypes) => {
      const currentFetchId = ++shiftsSentBySchedulerFetchId;
      set({ shiftsSentBySchedulerLoading: true, shiftsSentBySchedulerError: false });

      const query = {
        ...buildFilterQuery(dateRange, locationIds, positions, employmentTypes),
        page,
      };

      createDashboardsRequest<ShiftsSentBySchedulerResponse>({
        url: DASHBOARDS_AGENCY_SHIFTS_SENT_BY_SCHEDULER(),
        method: 'GET',
        query,
      }).then(
        (response) => {
          if (currentFetchId !== shiftsSentBySchedulerFetchId) return;
          const { meta } = response.data;
          set({
            shiftsSentByScheduler: mapShiftsSentByScheduler(response.data),
            shiftsSentBySchedulerPage: meta?.current_page ?? page,
            shiftsSentBySchedulerTotalPages: meta?.total_pages ?? 1,
            shiftsSentBySchedulerLoading: false,
            shiftsSentBySchedulerError: false,
          });
        },
        (error) => {
          if (currentFetchId !== shiftsSentBySchedulerFetchId) return;
          if (isDataLockError(error)) {
            set({
              shiftsSentByScheduler: AGENCY_DATA_LOCKED_STUB.shiftsSentByScheduler ?? null,
              shiftsSentBySchedulerPage: 1,
              shiftsSentBySchedulerTotalPages: 1,
              shiftsSentBySchedulerLoading: false,
              shiftsSentBySchedulerError: false,
            });
            return;
          }
          set({
            shiftsSentByScheduler: null,
            shiftsSentBySchedulerLoading: false,
            shiftsSentBySchedulerError: true,
          });
        },
      );
    },
    loadMoreShiftsByPosition: () => {
      const { shiftsByPositionLoadingMore, shiftsByPositionPage, shiftsByPositionTotalPages } =
        get();
      if (
        shiftsByPositionLoadingMore ||
        shiftsByPositionPage >= shiftsByPositionTotalPages ||
        !shiftsByPositionBaseQuery
      ) {
        return;
      }
      const currentFetchId = fetchId;
      const nextPage = shiftsByPositionPage + 1;
      set({ shiftsByPositionLoadingMore: true });

      fetchDashboardPage<AgencyShiftsByPositionResponse, AgencyShiftsByPositionResponseItem>({
        url: DASHBOARDS_AGENCY_SHIFTS_BY_POSITION(),
        query: shiftsByPositionBaseQuery,
        page: nextPage,
        selectItems: (body) => body.agency_shifts_by_position ?? [],
      })
        .then(({ items, meta }) => {
          if (currentFetchId !== fetchId) return;
          set((state) => ({
            shiftsByPosition: [...(state.shiftsByPosition ?? []), ...mapShiftsByPosition(items)],
            shiftsByPositionPage: meta?.current_page ?? nextPage,
            shiftsByPositionTotalPages: meta?.total_pages ?? state.shiftsByPositionTotalPages,
            shiftsByPositionLoadingMore: false,
          }));
        })
        .catch(() => {
          if (currentFetchId !== fetchId) return;
          set({ shiftsByPositionLoadingMore: false });
        });
    },
    setLoading: (isLoading) => set({ isLoading }),
    reset: () => {
      fetchId = 0;
      shiftsSentBySchedulerFetchId = 0;
      shiftsByPositionBaseQuery = null;
      set(INITIAL_STATE);
    },
  })),
);

export const useAgencyDashboardStore = agencyDashboardStore;
